import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Package, Printer, Search, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth, useCurrentUser } from "../auth";
import { useApi } from "../api";
import {
  ORGANIZATION_IMPERSONATE_PERMISSION,
  ORGANIZATION_LIST_PERMISSION,
  PRINTER_JOB_LABEL_PERMISSION,
  PRINTER_LIST_PERMISSION,
} from "../constants/permissions";
import {
  Button,
  Field,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
} from "./ui";
import logo from "../assets/validlabels_1.0_icon.png";

export function Brand() {
  return (
    <Link to="/products" className="vl-brand">
      <img src={logo} alt="" />
      <span>Valid Labels</span>
    </Link>
  );
}
export function LanguageSelect() {
  const { t, i18n } = useTranslation("ui");
  return (
    <Select
      aria-label={t("language")}
      value={i18n.resolvedLanguage || "en"}
      onValueChange={(value) => void i18n.changeLanguage(value)}
    >
      <SelectItem value="en">English</SelectItem>
      <SelectItem value="pl">Polski</SelectItem>
    </Select>
  );
}
function AccountMenu() {
  const { t } = useTranslation("ui");
  const { logout, user } = useAuth();
  const { currentUser, permissions, impersonatedOrgId, setImpersonatedOrgId } =
    useCurrentUser();
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [organizations, setOrganizations] = useState<
    { id: string; name: string }[]
  >([]);
  const [error, setError] = useState(false);
  const canSwitch =
    permissions.includes(ORGANIZATION_LIST_PERMISSION) &&
    permissions.includes(ORGANIZATION_IMPERSONATE_PERMISSION);
  useEffect(() => {
    if (!canSwitch) return;
    let alive = true;
    apiRef.current
      .get("/organizations")
      .then((data) => {
        if (alive) setOrganizations(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [canSwitch]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" aria-label={t("accountMenu")}>
          <span>{t("account")}</span>
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label={t("accountMenu")}>
        <div>
          <strong>
            {user?.profile.name || currentUser?.email || t("account")}
          </strong>
          {user?.profile.name && (
            <p className="vl-hint">{currentUser?.email}</p>
          )}
        </div>
        {canSwitch && (
          <Field label={t("organization")} htmlFor="account-organization">
            <Select
              id="account-organization"
              value={impersonatedOrgId || currentUser?.org_id || ""}
              onValueChange={(value) => setImpersonatedOrgId(value || null)}
            >
              <SelectItem value="" disabled={Boolean(currentUser?.org_id)}>
                {t("noOrganization")}
              </SelectItem>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </Select>
            {error && (
              <p className="vl-field-error">{t("organizationError")}</p>
            )}
          </Field>
        )}
        <Field label={t("language")}>
          <LanguageSelect />
        </Field>
        <Link to="/settings?section=account">{t("account")}</Link>
        <Button variant="outline" onClick={logout}>
          {t("signOut")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
export default function Layout({ children }: React.PropsWithChildren) {
  const { t } = useTranslation("ui");
  const { authenticated } = useAuth();
  const { permissions } = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();
  const previousPath = useRef(location.pathname);
  useEffect(() => {
    const shouldFocus =
      previousPath.current !== location.pathname &&
      !new URLSearchParams(location.search).has("focus");
    previousPath.current = location.pathname;
    let completed = false;
    const updateHeading = () => {
      const heading = document.querySelector<HTMLElement>("#main-content h1");
      if (!heading || completed) return;
      completed = true;
      document.title = `${heading.textContent || "Valid Labels"} · Valid Labels`;
      if (shouldFocus) {
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
        window.scrollTo(0, 0);
      }
    };
    const observer = new MutationObserver(updateHeading);
    observer.observe(document.getElementById("main-content") || document.body, {
      childList: true,
      subtree: true,
    });
    const frame = requestAnimationFrame(updateHeading);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [location.pathname]);
  const canPrint =
    permissions.includes(PRINTER_JOB_LABEL_PERMISSION) ||
    permissions.includes(PRINTER_LIST_PERMISSION);
  const focusSearch = () => {
    if (location.pathname !== "/products") navigate("/products?focus=search");
    else window.dispatchEvent(new Event("validlabels:focus-search"));
  };
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "k" &&
        authenticated
      ) {
        event.preventDefault();
        focusSearch();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [location.pathname, authenticated]);
  if (!authenticated)
    return (
      <div className="vl-public-shell">
        <a className="vl-skip" href="#main-content">
          {t("skip")}
        </a>
        <header className="vl-public-header">
          <Brand />
          <div style={{ width: 110 }}>
            <LanguageSelect />
          </div>
        </header>
        <main id="main-content" className="vl-public-main" tabIndex={-1}>
          {children}
        </main>
      </div>
    );
  return (
    <div className="vl-shell">
      <a className="vl-skip" href="#main-content">
        {t("skip")}
      </a>
      <header className="vl-app-header">
        <Brand />
        <div className="vl-topbar">
          <Button
            variant="ghost"
            className="vl-search-trigger"
            aria-label={`${t("search")} ⌘K`}
            onClick={focusSearch}
          >
            <Search />
            <span>{t("search")}</span>
            <kbd className="vl-hint" aria-hidden="true">
              ⌘K
            </kbd>
          </Button>
          <AccountMenu />
        </div>
      </header>
      <nav className="vl-nav" aria-label={t("navigation")}>
        <NavLink to="/products">
          <Package />
          {t("products")}
        </NavLink>
        {canPrint && (
          <NavLink to="/printing">
            <Printer />
            {t("print")}
          </NavLink>
        )}
        <NavLink to="/settings">
          <Settings />
          {t("settings")}
        </NavLink>
      </nav>
      <main id="main-content" className="vl-main" tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}
